// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 41-groups-management.md
// @greenlight-spec: 42-app-marketplace.md
// @greenlight-spec: decisions/0021-app-access-default-open-imperative-bindings.md
// @greenlight-spec: decisions/0064-group-scale-app-access-returns.md

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Globe, Lock } from 'lucide-react';
import type { AppAccessRole, AppGroupBinding } from '@greenlight/shared';

import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { OwnerAvatar } from '@/components/greenlight/OwnerAvatar';
import { AD_GROUP, AD_ROW } from '@/components/greenlight/surfaces';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { mutationErrorMessage } from '@/lib/apiErrors';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useSetAppVisibility, type AppVisibility } from '@/lib/useSetAppVisibility';

import { AccessGroupRow } from './AccessGroupRow';
import {
  AccessSearch,
  emailKey,
  groupKey,
  idpGroupKey,
  members,
  personKey,
  type AccessSelection,
} from './AccessSearch';
import { TabErrorPanel } from './tab-primitives';

/** What adding someone grants them. Viewers use the app; editors also change it. */
type Role = AppAccessRole;

/**
 * An editor grant to a whole group, held until the owner confirms it. Editor is
 * co-ownership — delete and ownership transfer — so handing it to everyone in a
 * group is never the same one click as viewer (ADR-0064 amendment A).
 */
type EditorConfirm = { name: string; memberCount: number; source: GroupSource } & (
  | { action: 'bind'; candidate: AccessSelection }
  | { action: 'promote'; binding: AppGroupBinding }
);

type GroupSource = AppGroupBinding['source'];

/**
 * What the owner is about to hand over. A Greenlight group's count is its
 * membership; a directory group's is only the people Greenlight has seen sign
 * in, so the copy names the group rather than the number it cannot complete
 * (ADR-0064 amendment D).
 */
const confirmCopy = ({ name, memberCount, source }: EditorConfirm): string =>
  source === 'idp'
    ? `Editor is co-ownership. Everyone in ${name} would be able to change this app, transfer it, and delete it. Greenlight has seen ${members(memberCount)} sign in so far; the directory group may hold more.`
    : `Editor is co-ownership. All ${members(memberCount)} of ${name} would be able to change this app, transfer it, and delete it.`;

export interface AppAccessCardProps {
  appId: string;
  ownerEmail: string | null;
  ownerName?: string | null;
  /** Server values; the general-access select below owns the live state. */
  accessRestricted: boolean;
}

/**
 * Who can reach this app, in one place: one list of entries — people and the
 * groups that admit them — each with the role it holds, and one control for
 * everyone else. One search resolves all three kinds, so an owner names a
 * colleague or a team in the same box and never meets a directory identifier.
 */
export function AppAccessCard({
  appId,
  ownerEmail,
  ownerName,
  accessRestricted,
}: AppAccessCardProps): React.ReactElement {
  const queryClient = useQueryClient();
  const visibility = useSetAppVisibility(appId, { accessRestricted });
  const coOwners = api.coOwners.list.useQuery(['co-owners', appId], { params: { id: appId } });
  const grants = api.accessRequests.list.useQuery(['access-requests', appId], {
    params: { id: appId },
  });
  const bindings = api.rbac.list.useQuery(['rbac-groups', appId], { params: { id: appId } });

  const addCoOwner = api.coOwners.add.useMutation();
  const removeCoOwner = api.coOwners.remove.useMutation();
  const grantByEmail = api.accessRequests.grantByEmail.useMutation();
  const revokeGrant = api.accessRequests.revokeById.useMutation();
  const bindGroup = api.rbac.add.useMutation();
  const setBindingRole = api.rbac.updateRole.useMutation();
  const unbindGroup = api.rbac.remove.useMutation();

  const [role, setRole] = React.useState<Role>('viewer');
  const [error, setError] = React.useState<string | null>(null);
  const [confirm, setConfirm] = React.useState<EditorConfirm | null>(null);

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['co-owners', appId] });
    await queryClient.invalidateQueries({ queryKey: ['access-requests', appId] });
    await queryClient.invalidateQueries({ queryKey: ['rbac-groups', appId] });
  };

  if (coOwners.isError || grants.isError || bindings.isError) {
    return <TabErrorPanel message="Couldn't load who can access this app." />;
  }
  if (coOwners.isLoading || grants.isLoading || bindings.isLoading) {
    return <Skeleton className="h-56 rounded-gl-lg" />;
  }

  const coOwnerRows = coOwners.data?.status === 200 ? coOwners.data.body.items : [];
  const editorsTruncated =
    coOwners.data?.status === 200 && coOwners.data.body.group_members_truncated;
  // Highest role wins, in the list as in the check (docs/41 §Role): someone named
  // as a viewer who an editor binding also reaches is an editor, and showing the
  // viewer row too would offer a revocation that leaves the editor role standing.
  const editorEmails = new Set(coOwnerRows.map((c) => c.email.toLowerCase()));
  const grantRows =
    grants.data?.status === 200
      ? grants.data.body.items.filter(
          (g) => g.status === 'granted' && !editorEmails.has(g.user_email.toLowerCase()),
        )
      : [];
  const groupRows = bindings.data?.status === 200 ? bindings.data.body.items : [];

  const listed = new Set<string>([
    // The owner is on the list but in none of the three reads — the co-owner
    // endpoint never returns them — and granting yourself access writes a
    // second, removable-looking row beside the fixed Owner one.
    ...(ownerEmail ? [emailKey(ownerEmail)] : []),
    ...coOwnerRows.flatMap((c) => [emailKey(c.email), personKey(c.user_id)]),
    ...grantRows.flatMap((g) => [emailKey(g.user_email), personKey(g.user_id)]),
    ...groupRows.map((b) =>
      b.source === 'idp'
        ? idpGroupKey(b.idp_group_registration_id ?? '')
        : groupKey(b.group_id ?? ''),
    ),
  ]);

  const grantTo = async (to: string): Promise<void> => {
    const res = await grantByEmail.mutateAsync({ params: { id: appId }, body: { email: to } });
    if (res.status !== 200) throw res;
  };
  const coOwn = async (to: string): Promise<void> => {
    const res = await addCoOwner.mutateAsync({ params: { id: appId }, body: { email: to } });
    if (res.status !== 201) throw res;
  };
  const revoke = async (grantId: string): Promise<void> => {
    const res = await revokeGrant.mutateAsync({ params: { grantId }, body: {} });
    if (res.status !== 200) throw res;
  };
  const unCoOwn = async (userId: string): Promise<void> => {
    const res = await removeCoOwner.mutateAsync({ params: { id: appId, userId } });
    if (res.status !== 204) throw res;
  };
  const bind = async (candidate: AccessSelection, as: Role): Promise<void> => {
    const body =
      candidate.kind === 'group'
        ? { group_id: candidate.group_id, role: as }
        : candidate.kind === 'idp_group'
          ? { idp_group_registration_id: candidate.idp_group_registration_id, role: as }
          : undefined;
    if (!body) throw new Error('Invariant: only a group candidate is bound');
    const res = await bindGroup.mutateAsync({ params: { id: appId }, body });
    if (res.status !== 201) throw res;
  };
  const rebind = async (bindingId: string, as: Role): Promise<void> => {
    const res = await setBindingRole.mutateAsync({
      params: { id: appId, bindingId },
      body: { role: as },
    });
    if (res.status !== 200) throw res;
  };
  const unbind = async (bindingId: string): Promise<void> => {
    const res = await unbindGroup.mutateAsync({ params: { id: appId, bindingId } });
    if (res.status !== 204) throw res;
  };

  const run = async (work: () => Promise<void>, done: string): Promise<void> => {
    setError(null);
    try {
      await work();
      await invalidate();
      toast.success(done);
    } catch (err) {
      setError(mutationErrorMessage(err));
    }
  };

  const addPerson = (email: string): void =>
    void run(
      () => (role === 'editor' ? coOwn(email) : grantTo(email)),
      role === 'editor' ? `${email} is now an editor.` : `${email} can now use this app.`,
    );

  const addGroup = (candidate: AccessSelection, as: Role, name: string): void =>
    void run(
      () => bind(candidate, as),
      as === 'editor'
        ? `Everyone in ${name} can now change this app.`
        : `Everyone in ${name} can now use this app.`,
    );

  const select = (candidate: AccessSelection): void => {
    if (candidate.kind === 'person' || candidate.kind === 'email') {
      addPerson(candidate.email);
      return;
    }
    if (role === 'editor') {
      setConfirm({
        action: 'bind',
        name: candidate.name,
        memberCount: candidate.member_count,
        source: candidate.kind === 'idp_group' ? 'idp' : 'greenlight',
        candidate,
      });
      return;
    }
    addGroup(candidate, 'viewer', candidate.name);
  };

  // Role changes on a person are add-then-drop rather than an update: editor
  // access and a grant are separate rows, so promoting keeps them reachable
  // throughout. A group's role is one column, so it is a single write.
  const promote = (grantId: string, to: string, who: string): void =>
    void run(async () => {
      await coOwn(to);
      await revoke(grantId);
    }, `${who} is now an editor.`);

  const demote = (userId: string, to: string, who: string): void =>
    void run(async () => {
      await grantTo(to);
      await unCoOwn(userId);
    }, `${who} can use this app, but no longer change it.`);

  const changeGroupRole = (binding: AppGroupBinding, next: Role | 'remove'): void => {
    if (next === 'remove') {
      void run(() => unbind(binding.id), `${binding.name} no longer has access.`);
    } else if (next === 'editor') {
      setConfirm({
        action: 'promote',
        name: binding.name,
        memberCount: binding.member_count,
        source: binding.source,
        binding,
      });
    } else {
      void run(
        () => rebind(binding.id, 'viewer'),
        `${binding.name} can use this app, but no longer change it.`,
      );
    }
  };

  const confirmEditor = (): void => {
    if (!confirm) return;
    const pending = confirm;
    setConfirm(null);
    if (pending.action === 'bind') {
      addGroup(pending.candidate, 'editor', pending.name);
    } else {
      void run(
        () => rebind(pending.binding.id, 'editor'),
        `Everyone in ${pending.name} can now change this app.`,
      );
    }
  };

  return (
    <div className="space-y-6" data-testid="app-access-card">
      <GeneralAccess
        value={visibility.value}
        onChange={(next) => void visibility.set(next)}
        pending={visibility.isPending}
      />

      <div className="flex flex-col gap-2 py-1 sm:flex-row sm:items-center">
        <AccessSearch appId={appId} onSelect={select} excludeKeys={listed} />
        <div className="flex shrink-0 gap-2">
          <Select value={role} onValueChange={(v) => setRole(v as Role)}>
            <SelectTrigger className="h-11 w-36 md:h-9" aria-label="Role for what you add next">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="viewer">Viewer</SelectItem>
              <SelectItem value="editor">Editor</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <section className="space-y-2">
        <h3 className="text-[13px] font-medium">People and groups with access</h3>
        <div className={AD_GROUP}>
          {ownerEmail && <PersonRow email={ownerEmail} name={ownerName} fixedRole="Owner" />}
          {coOwnerRows.map((c) => (
            <PersonRow
              key={c.user_id}
              email={c.email}
              name={c.display_name}
              role="editor"
              via={c.via_group?.name}
              onRoleChange={(next) =>
                next === 'remove'
                  ? void run(
                      () => unCoOwn(c.user_id),
                      `${c.display_name ?? c.email} no longer has access.`,
                    )
                  : demote(c.user_id, c.email, c.display_name ?? c.email)
              }
            />
          ))}
          {grantRows.map((g) => (
            <PersonRow
              key={g.id}
              email={g.user_email}
              name={g.user_name}
              role="viewer"
              onRoleChange={(next) =>
                next === 'remove'
                  ? void run(
                      () => revoke(g.id),
                      `${g.user_name ?? g.user_email} no longer has access.`,
                    )
                  : promote(g.id, g.user_email, g.user_name ?? g.user_email)
              }
            />
          ))}
          {groupRows.map((b) => (
            <AccessGroupRow
              key={b.id}
              appId={appId}
              binding={b}
              onRoleChange={(next) => changeGroupRole(b, next)}
            />
          ))}
        </div>
        {editorsTruncated && (
          <p className="text-[12px] text-muted-foreground" data-testid="access-editors-truncated">
            A group bound to this app reaches more editors than are shown here.
          </p>
        )}
        <p className="text-[12px] text-muted-foreground" data-testid="access-bypass-note">
          You, this app&rsquo;s editors, and your IT admins always have access. Changes here take
          effect within about a minute.
        </p>
      </section>

      {error && (
        <p className="text-sm text-destructive" role="alert" data-testid="app-access-error">
          {error}
        </p>
      )}

      <GLDrawer
        variant="confirm"
        open={confirm !== null}
        onOpenChange={(open) => {
          if (!open) setConfirm(null);
        }}
        title={confirm ? `Make ${confirm.name} an editor?` : ''}
        description={confirm ? confirmCopy(confirm) : undefined}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button onClick={confirmEditor} data-testid="access-editor-confirm">
              Make editor
            </Button>
          </>
        }
      >
        {null}
      </GLDrawer>
    </div>
  );
}

const VISIBILITY_COPY: Record<
  AppVisibility,
  { icon: typeof Globe; label: string; detail: string }
> = {
  marketplace: {
    icon: Globe,
    label: 'Anyone in your organization',
    detail: 'Listed in the marketplace, so anyone here can find it and open it.',
  },
  restricted: {
    icon: Lock,
    label: 'Only the people listed below',
    detail: 'Nobody else in your organization can find it or open it.',
  },
};

/**
 * Reach for everyone not named on the app. The two options are exclusive and
 * cover the whole space, so the app's reach is stated outright rather than
 * inferred from which rows happen to exist.
 */
function GeneralAccess({
  value,
  onChange,
  pending,
}: {
  value: AppVisibility;
  onChange: (next: AppVisibility) => void;
  pending: boolean;
}): React.ReactElement {
  const { icon: Icon, detail } = VISIBILITY_COPY[value];
  return (
    <section data-testid="app-general-access">
      <div className="flex items-start gap-3 rounded-gl-lg border border-border/70 p-4">
        <Icon className="mt-2.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Select
            value={value}
            onValueChange={(v) => onChange(v as AppVisibility)}
            disabled={pending}
          >
            <SelectTrigger
              className="h-11 w-full md:h-9 md:w-[22rem]"
              aria-label="Who can use this app"
              data-testid="app-visibility-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(VISIBILITY_COPY) as AppVisibility[]).map((key) => (
                <SelectItem key={key} value={key}>
                  {VISIBILITY_COPY[key].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[12px] text-muted-foreground">{detail}</p>
        </div>
      </div>
    </section>
  );
}

function PersonRow({
  email,
  name,
  role,
  fixedRole,
  via,
  onRoleChange,
}: {
  email: string;
  name?: string | null;
  role?: Role;
  /** Rendered as static text for people whose role cannot change here. */
  fixedRole?: string;
  /**
   * A group that makes this person an editor. The row is then read-only even if
   * they also hold a direct grant: no control here ends the role while the
   * binding stands, so unbinding the group comes first (docs/41 §Role).
   */
  via?: string;
  onRoleChange?: (next: Role | 'remove') => void;
}): React.ReactElement {
  const who = name ?? email;
  const staticRole = fixedRole ?? (via ? `Editor · via ${via}` : undefined);
  return (
    <div
      className={cn(AD_ROW, 'justify-between')}
      data-testid={`access-row-${(role ?? fixedRole ?? '').toLowerCase()}`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <OwnerAvatar email={email} />
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold leading-snug">{who}</div>
          <div className="truncate text-[12px] text-muted-foreground">{email}</div>
        </div>
      </div>
      {staticRole ? (
        <span className="shrink-0 px-1 text-[12px] text-muted-foreground">{staticRole}</span>
      ) : (
        <Select value={role} onValueChange={(v) => onRoleChange?.(v as Role | 'remove')}>
          <SelectTrigger className="h-9 w-36 shrink-0" aria-label={`Role for ${who}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="viewer">Viewer</SelectItem>
            <SelectItem value="editor">Editor</SelectItem>
            <SelectItem value="remove">Remove access</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
